import React, { useState, useEffect } from "react";
import { GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { format } from "date-fns";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import { META_DESCRIPTION, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { sliceMetaDescription } from "@app/utilities/helpers";
import { getBlogs, getPopularBlogs } from "@app/http/cms";
import { Blog } from "@models/Blog";
import PaginationComp from "@components/navs/PaginationComp";
import { IPaginationBase } from "@models/interfaces/IPagination";
import _ from "lodash";

const LIMIT = 10;

interface IBlogsPage {
  blogs: Blog[];
  popularBlogs: Blog[];
  totalCount: number;
}

const BlogsPage: NextPage<IBlogsPage> = (props: IBlogsPage) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(_.clamp(Number(router.query.page) || 1, 1, 100));
  const [loading, setLoading] = useState(false);
  const [blogs, setBlogs] = useState(props.blogs);

  const { blogs: initialBlogs, popularBlogs, totalCount } = props;
  const pagination: IPaginationBase = {
    page: currentPage,
    rowsPerPage: LIMIT,
    totalCount: totalCount,
  };

  useEffect(() => {
    const page = Number(router.query.page) || 1;
    setCurrentPage(page);
  }, [router.query.page]);

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/blogs?page=${currentPage}&limit=${LIMIT}`);
        const data = await response.json();
        setBlogs(data.blogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
      setLoading(false);
    };

    if (currentPage > 1) {
      fetchBlogs();
    } else {
      setBlogs(initialBlogs);
    }
  }, [currentPage, initialBlogs]);

  const onHandlePageChanged = (page: number) => {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, page },
      },
      undefined,
      { shallow: true }
    );
  };

  /**
   * Meta contents
   */
  const metaTitle = `Blogs | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/blogs`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Blogs">
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaImage} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaImage} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
        {blogs?.length > 0 &&
          blogs.map((blog) => (
            <script
              key={blog.slug}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(blog.schema),
              }}
            ></script>
          ))}
      </Head>
      <div className="blogs-page" data-testid="blogs-page">
        <div className="blogs-list">
          {loading
            ? Array(LIMIT)
                .fill(0)
                .map((_, idx) => (
                  <div key={idx} className="card blog-card">
                    <div className="row">
                      <div className="col-md-4 col-xl-3 img-container">
                        <div className="skeleton-image animate-pulse" style={{ height: "150px", width: "150px" }} />
                      </div>
                      <div className="col-md-8 col-xl-9">
                        <div className="card-body">
                          <div className="skeleton-title animate-pulse mb-3" style={{ height: "24px", width: "70%" }} />
                          <div className="skeleton-text animate-pulse mb-2" style={{ height: "16px", width: "90%" }} />
                          <div className="skeleton-text animate-pulse mb-2" style={{ height: "16px", width: "85%" }} />
                          <div className="skeleton-text animate-pulse" style={{ height: "14px", width: "40%" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            : blogs.map((blog, idx) => {
                const bodyText = sliceMetaDescription(blog.content.text, 210),
                  publishedDateStr = format(blog.createdAt, "PP");

                return (
                  <Link key={idx} href={`/blogs/${blog.slug}`}>
                    <a className="card blog-card">
                      <div className="row">
                        <div className="col-md-4 col-xl-3 img-container">
                          <img className="img-blog" src={blog.featuredImage?.src || ""} alt={blog.title} height={150} width={150} />
                        </div>
                        <div className="col-md-8 col-xl-9">
                          <div className="card-body">
                            <h5 className="card-title">{blog.title}</h5>
                            <p className="card-text">{bodyText}</p>
                            <p className="card-text">
                              <small className="text-muted">Published on {publishedDateStr}</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </Link>
                );
              })}
          <div className="pagination-container">
            <PaginationComp pagination={pagination} onPageChanged={onHandlePageChanged} />
          </div>
        </div>
        {popularBlogs?.length && (
          <div className="popular-blogs-list">
            <h5 className="header text-muted">Popular Blogs</h5>
            <div className="">
              {popularBlogs.map((blog, idx) => {
                return (
                  <Link key={idx} href={`/blogs/${blog.slug}`}>
                    <a className="blog-link">
                      <img className="img-blog" src={blog.featuredImage?.src || ""} alt={blog.title} height={70} width={70} />
                      <div className="blog-body">
                        <h5 className="blog-title">{blog.title}</h5>
                      </div>
                    </a>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const [ssrBlogs, ssrPopularBlogs] = await Promise.all([getBlogs(1, LIMIT), getPopularBlogs()]);

  return {
    props: {
      blogs: ssrBlogs.blogs || [],
      totalCount: ssrBlogs.totalCount || 0,
      popularBlogs: ssrPopularBlogs || [],
    } as IBlogsPage,
    notFound: !ssrBlogs.blogs.length,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export default BlogsPage;
